/**
 * One page editing session (plan 3.3, 7.3), platform-free: the web iframe
 * and the native WebView hand it every message the frame posts and give it
 * a way to post to the frame. It
 *
 * - acts only on messages `parseArtifactEditorFrameMessage` accepts (this
 *   session's token, the frame's own window where the platform says which
 *   window posted, and the bridge's schema); everything else is ignored;
 * - keeps what the UI draws: whether the frame is ready, the focused element
 *   (for the element bar), the selection (for the selection toolbar), the
 *   last link or image clicked, and a length limit hit;
 * - turns text changes and element bar actions into ops for the queue, and
 *   mirrors the element bar's ops in the frame (`apply`), so the page
 *   changes at once and the server stores the same thing;
 * - passes the ids a save answered to the frame (`renumber`).
 */
import type { PageEditImageSource, PageEditOp, PageEditsResult } from '@tabbio/types';
import {
  ARTIFACT_EDITOR_BRIDGE_VERSION,
  parseArtifactEditorFrameMessage,
  type ArtifactEditorFrameMessage,
  type ArtifactEditorParentMessage,
} from '@tabbio/utils/artifact-editor-bridge';
import type { PageEditQueue } from './page-edit-queue';

export type EditorRect = { x: number; y: number; width: number; height: number };

export type EditorElement = NonNullable<Extract<ArtifactEditorFrameMessage, { type: 'element' }>['element']>;

export type EditorSelection = { id: string; text: string; rect: EditorRect };

export type EditorLink = { id: string; href: string; text: string; rect: EditorRect };

export type EditorImage = { id: string; src: string; alt: string; rect: EditorRect };

export type PageEditorSnapshot = {
  ready: boolean;
  /** Text blocks the bridge marked editable. */
  blocks: number;
  element: EditorElement | null;
  selection: EditorSelection | null;
  /** The last link clicked in edit mode (Edit link reads its text). */
  link: EditorLink | null;
  /** The last image clicked in edit mode. */
  image: EditorImage | null;
  /** A block over its length limit: its changes wait until it is shorter. */
  limit: { id: string; max: number } | null;
};

export const INITIAL_PAGE_EDITOR_SNAPSHOT: PageEditorSnapshot = Object.freeze({
  ready: false,
  blocks: 0,
  element: null,
  selection: null,
  link: null,
  image: null,
  limit: null,
});

/** A parent message without its envelope (the session adds source, v and token). */
type ParentBody =
  | { type: 'setMode'; mode: 'edit' | 'view'; accent?: string }
  | { type: 'focus'; id: string; edit?: boolean }
  | { type: 'blur' }
  | { type: 'apply'; op: PageEditOp & { displaySrc?: string } }
  | { type: 'renumber'; ids: Record<string, string | null> };

export type PageEditorSessionOptions = {
  token: string;
  queue: PageEditQueue;
  /** Posts to the frame: `iframe.contentWindow.postMessage(message, '*')` or the WebView's postMessage. */
  post: (message: ArtifactEditorParentMessage) => void;
  onChange?: (snapshot: PageEditorSnapshot) => void;
  /** The window that must have posted a message (web); undefined where the platform does not say (native). */
  expectedSource?: () => unknown;
};

/** An https, http or mailto link, or a fragment (what the server and the bridge accept on `href`). */
export function isAllowedLinkHref(value: string): boolean {
  const href = value.trim();
  if (!href || href.length > 2_048) return false;
  if (href.startsWith('#')) return !/\s/.test(href);
  if (!/^(https?:\/\/|mailto:)/i.test(href)) return false;
  try {
    const url = new URL(href);
    return url.protocol === 'mailto:' || Boolean(url.hostname);
  } catch {
    return false;
  }
}

/** An https image link (the only kind of URL a replaced image may use). */
export function isAllowedImageUrl(value: string): boolean {
  const url = value.trim();
  if (!/^https:\/\//i.test(url) || url.length > 2_048) return false;
  try {
    return Boolean(new URL(url).hostname);
  } catch {
    return false;
  }
}

export class PageEditorSession {
  private snapshot: PageEditorSnapshot = INITIAL_PAGE_EDITOR_SNAPSHOT;
  private mode: 'edit' | 'view' = 'view';
  /**
   * The element focused by the click that also posted `link` or `image`
   * (the bridge posts those first); the link or image stays offered while
   * that element keeps the focus.
   */
  private clickOwner: string | null | undefined;
  private accent: string | undefined;

  constructor(private readonly options: PageEditorSessionOptions) {}

  get state(): PageEditorSnapshot {
    return this.snapshot;
  }

  get token(): string {
    return this.options.token;
  }

  /**
   * A message from the frame. Returns the parsed message when it was acted
   * on, null when it was ignored (foreign window, wrong token, bad shape).
   */
  receive(data: unknown, source?: unknown): ArtifactEditorFrameMessage | null {
    const expected = this.options.expectedSource?.();
    const message = parseArtifactEditorFrameMessage(data, {
      token: this.options.token,
      ...(this.options.expectedSource ? { source, expectedSource: expected } : {}),
    });
    if (!message) return null;
    switch (message.type) {
      case 'ready':
        this.update({ ready: true, blocks: message.blocks });
        // A reloaded frame starts in view mode: put it back where we are.
        this.send({ type: 'setMode', mode: this.mode, ...(this.accent ? { accent: this.accent } : {}) });
        break;
      case 'element': {
        const id = message.element?.id ?? null;
        if (this.clickOwner === undefined && (this.snapshot.link || this.snapshot.image)) {
          // The first element after a link or image click is the one it belongs to.
          this.clickOwner = id;
          this.update({ element: message.element });
        } else if (this.clickOwner !== undefined && id !== this.clickOwner) {
          this.clickOwner = undefined;
          this.update({ element: message.element, link: null, image: null });
        } else {
          this.update({ element: message.element });
        }
        break;
      }
      case 'selection':
        this.update({
          selection: message.id && message.rect && message.text.trim() ? { id: message.id, text: message.text, rect: message.rect } : null,
        });
        break;
      case 'change':
        this.options.queue.enqueue(message.op);
        if (this.snapshot.limit?.id === message.op.id) this.update({ limit: null });
        break;
      case 'image':
        this.clickOwner = undefined;
        this.update({ image: { id: message.id, src: message.src, alt: message.alt, rect: message.rect }, link: null });
        break;
      case 'link':
        this.clickOwner = undefined;
        this.update({ link: { id: message.id, href: message.href, text: message.text, rect: message.rect }, image: null });
        break;
      case 'limit':
        this.update({ limit: { id: message.id, max: message.max } });
        break;
    }
    return message;
  }

  setMode(mode: 'edit' | 'view', accent?: string) {
    this.mode = mode;
    this.accent = accent;
    this.send({ type: 'setMode', mode, ...(accent ? { accent } : {}) });
    if (mode === 'view') this.update({ element: null, selection: null, link: null, image: null });
  }

  focus(id: string, edit = false) {
    this.send({ type: 'focus', id, ...(edit ? { edit: true } : {}) });
  }

  blur() {
    this.send({ type: 'blur' });
    this.update({ element: null, selection: null });
  }

  // ── The element bar ─────────────────────────────────────────────────────

  move(id: string, direction: 'up' | 'down') {
    this.applyAndQueue({ op: 'move', id, direction });
  }

  duplicate(id: string) {
    this.applyAndQueue({ op: 'duplicate', id });
  }

  remove(id: string) {
    this.applyAndQueue({ op: 'remove', id });
    this.update({ element: null, selection: null, link: null, image: null });
  }

  /** Edit link: the address, and the words when they changed. */
  editLink(id: string, href: string, text?: string) {
    const value = href.trim();
    if (!isAllowedLinkHref(value)) return false;
    this.applyAndQueue({ op: 'setAttr', id, name: 'href', value });
    const link = this.snapshot.link?.id === id ? this.snapshot.link : null;
    if (typeof text === 'string' && text.trim() && (!link || text !== link.text)) {
      this.applyAndQueue({ op: 'setText', id, text: text.trim() });
    }
    this.update({ link: link ? { ...link, href: value, text: text?.trim() || link.text } : null });
    return true;
  }

  setAlt(id: string, alt: string) {
    this.applyAndQueue({ op: 'setAttr', id, name: 'alt', value: alt.trim().slice(0, 300) });
    const image = this.snapshot.image?.id === id ? this.snapshot.image : null;
    if (image) this.update({ image: { ...image, alt: alt.trim() } });
  }

  /**
   * Replace image: one of the user's images (`displaySrc` is a link the
   * frame can show, since `tabbio-artifact:` only resolves on the server) or
   * an https link.
   */
  replaceImage(id: string, source: PageEditImageSource, displaySrc: string, alt?: string) {
    if (source.type === 'url' && !isAllowedImageUrl(source.url)) return false;
    const op: Extract<PageEditOp, { op: 'replaceImage' }> = {
      op: 'replaceImage',
      id,
      source,
      ...(typeof alt === 'string' ? { alt: alt.slice(0, 300) } : {}),
    };
    this.send({ type: 'apply', op: { ...op, displaySrc } });
    this.options.queue.enqueue(op);
    const image = this.snapshot.image?.id === id ? this.snapshot.image : null;
    if (image) this.update({ image: { ...image, src: displaySrc, alt: alt ?? image.alt } });
    return true;
  }

  /** Mirror ops made outside the frame (an applied suggestion) and queue them. */
  applyOps(ops: readonly PageEditOp[]) {
    for (const op of ops) this.applyAndQueue(op);
  }

  /** After a save: the ids of the base that changed, for the frame. */
  saved(result: PageEditsResult) {
    const ids = result.ids;
    if (ids && Object.keys(ids).length > 0) {
      this.send({ type: 'renumber', ids });
      const remap = (id: string | undefined) =>
        id && Object.prototype.hasOwnProperty.call(ids, id) ? ids[id] : id;
      const element = this.snapshot.element;
      const nextElementId = element ? remap(element.id) : null;
      this.update({
        element: element && nextElementId ? { ...element, id: nextElementId } : null,
        selection: null,
      });
    }
  }

  /** A new frame is loading (reload, conflict, another version). */
  reset() {
    this.clickOwner = undefined;
    this.snapshot = INITIAL_PAGE_EDITOR_SNAPSHOT;
    this.options.onChange?.(this.snapshot);
  }

  private applyAndQueue(op: PageEditOp) {
    this.send({ type: 'apply', op });
    this.options.queue.enqueue(op);
  }

  private send(body: ParentBody) {
    this.options.post({
      source: 'tabbio-editor-parent',
      v: ARTIFACT_EDITOR_BRIDGE_VERSION,
      token: this.options.token,
      ...body,
    } as ArtifactEditorParentMessage);
  }

  private update(patch: Partial<PageEditorSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    this.options.onChange?.(this.snapshot);
  }
}
